import { Application, Assets, Container, Graphics, Sprite, Text } from 'pixi.js';
import 'pixi.js/unsafe-eval';
import { themeAssets } from './assets.js';

export function displayedSymbol(symbol, languageCode) {
  if (symbol === 'JK') return 'JK';
  return languageCode.startsWith('ka') ? symbol : symbol.toLocaleUpperCase(languageCode);
}

export class LetterBoard {
  constructor(hostElement, onSelectTile, onChangeTile) {
    this.hostElement = hostElement;
    this.onSelectTile = onSelectTile;
    this.onChangeTile = onChangeTile;
    this.tileViews = [];
    this.selectedIds = new Set();
    this.disabled = false;
  }

  async initialize() {
    this.application = new Application();
    await this.application.init({ width: this.hostElement.clientWidth || 400, height: 300,
      backgroundAlpha: 0, antialias: true, autoDensity: true, resolution: Math.min(window.devicePixelRatio || 1, 2), autoStart: false });
    this.texture = await Assets.load(themeAssets.tile);
    this.application.canvas.setAttribute('aria-hidden', 'true');
    this.hostElement.appendChild(this.application.canvas);
    this.resizeObserver = new ResizeObserver(() => {
      if (this.hostElement.clientWidth !== this.lastWidth) this.layout();
    });
    this.resizeObserver.observe(this.hostElement);
  }

  renderTiles(tiles, languageCode, disabled) {
    this.disabled = disabled;
    cancelAnimationFrame(this.selectionFrame);
    if (this.animation) { this.application.ticker.remove(this.animation); this.application.stop(); }
    for (const view of this.tileViews) view.container.destroy({ children: true });
    const occurrences = new Map();
    const totals = new Map();
    for (const tile of tiles) totals.set(tile.symbol, (totals.get(tile.symbol) ?? 0) + 1);
    this.tileViews = tiles.map(tile => {
      occurrences.set(tile.symbol, (occurrences.get(tile.symbol) ?? 0) + 1);
      const container = new Container();
      const shadow = new Graphics();
      const background = new Sprite(this.texture);
      const frame = new Graphics();
      const selection = new Graphics();
      const change = new Container();
      change.addChild(new Graphics().circle(0, 0, 10).fill({ color: 0x123f36, alpha: .92 }));
      const changeIcon = new Text({ text: '↻', style: { fontFamily: 'Arial', fontSize: 16, fill: 0xfff4d9 } });
      changeIcon.anchor.set(.5);
      change.addChild(changeIcon);
      change.on('pointertap', event => { event.stopPropagation(); if (!this.disabled && this.assistanceEnabled) this.onChangeTile(tile); });
      const letterText = new Text({ text: displayedSymbol(tile.symbol, languageCode), style: { fontFamily: ['Segoe UI', 'Arial'], fontSize: 48, fontWeight: '700', fill: tile.symbol === 'JK' ? 0x9c630b : 0x073b36 } });
      const pointsText = new Text({ text: String(tile.points), style: { fontFamily: ['Segoe UI', 'Arial'], fontSize: 17, fontWeight: '700', fill: 0x123f36 } });
      const duplicateText = new Text({ text: totals.get(tile.symbol) > 1 ? String(occurrences.get(tile.symbol)) : '', style: { fontFamily: 'Arial', fontSize: 11, fill: 0x97713b } });
      letterText.anchor.set(0.5);
      pointsText.anchor.set(1, 1);
      container.addChild(shadow, background, frame, letterText, pointsText, duplicateText, selection, change);
      container.eventMode = disabled ? 'none' : 'static';
      container.cursor = 'pointer';
      container.on('pointertap', () => { if (!this.disabled) this.onSelectTile(tile); });
      container.on('pointerover', () => { if (!this.disabled && !this.selectedIds.has(tile.id)) { background.tint = 0xffffff; frame.alpha = 0.75; this.application.render(); } });
      container.on('pointerout', () => { this.paintSelection(view); this.application.render(); });
      this.application.stage.addChild(container);
      const view = { tile, container, shadow, background, frame, selection, letterText, pointsText, duplicateText, change };
      return view;
    });
    this.layout();
    let elapsedSeconds = 0;
    this.animation = ticker => {
      elapsedSeconds += ticker.deltaMS / 1000;
      const progress = Math.min(1, elapsedSeconds / 0.22);
      for (const view of this.tileViews) { view.container.alpha = progress; view.container.y = view.targetY + (1 - progress) * 8; }
      if (progress === 1) { this.application.ticker.remove(this.animation); this.application.stop(); }
    };
    this.application.ticker.add(this.animation);
    this.application.start();
  }

  layout() {
    if (!this.tileViews.length || !this.hostElement.clientWidth) return;
    const width = this.hostElement.clientWidth;
    this.lastWidth = width;
    const preferredColumns = this.tileViews.length <= 9 ? 3 : this.tileViews.length <= 16 ? 4 : width >= 480 ? 6 : 4;
    const columns = Math.min(this.tileViews.length, preferredColumns, Math.max(1, Math.floor((width - 16) / 60)));
    const mobile = window.matchMedia('(max-width: 760px)').matches;
    const gap = mobile ? 8 : width < 360 ? 10 : 14;
    const tileSize = Math.min(mobile ? 76 : 138, (width - 36 - (columns - 1) * gap) / columns);
    const rows = Math.ceil(this.tileViews.length / columns);
    const height = rows * (tileSize + gap) - gap + (mobile ? 28 : 44);
    this.hostElement.style.height = `${height}px`;
    this.application.renderer.resize(width, height);
    this.tileViews.forEach((view, index) => {
      const row = Math.floor(index / columns);
      const rowCount = Math.min(columns, this.tileViews.length - row * columns);
      const offset = (width - rowCount * tileSize - (rowCount - 1) * gap) / 2;
      view.container.position.set(offset + (index % columns) * (tileSize + gap), (mobile ? 10 : 18) + row * (tileSize + gap));
      view.targetY = view.container.y;
      view.shadow.clear().roundRect(1, 6, tileSize, tileSize, tileSize * 0.13).fill({ color: 0x021e1a, alpha: 0.45 });
      view.background.width = tileSize;
      view.background.height = tileSize;
      view.letterText.position.set(tileSize / 2, tileSize * 0.43);
      view.letterText.style.fontSize = tileSize * (Array.from(view.tile.symbol).length > 1 ? 0.34 : 0.56);
      view.pointsText.position.set(tileSize - 10, tileSize - 11);
      view.pointsText.style.fontSize = Math.max(13, tileSize * 0.18);
      view.duplicateText.position.set(9, 8);
      view.change.position.set(15, tileSize - 15);
      view.change.visible = Boolean(this.assistanceVisible) && !(this.jokerEnabled && view.tile.symbol === 'JK');
      view.change.eventMode = this.assistanceEnabled ? 'static' : 'none';
      view.change.cursor = this.assistanceEnabled ? 'pointer' : 'default';
      this.paintSelection(view);
    });
    this.application.render();
  }

  paintSelection(view) {
    const size = view.background.width;
    const selected = this.selectedIds.has(view.tile.id);
    view.frame.clear().roundRect(0, 0, size, size, size * 0.13).stroke({ color: selected ? 0x45c48a : 0xffefd1, width: selected ? 4 : 1, alpha: selected ? 1 : 0.22 });
    view.frame.alpha = 1;
    view.background.tint = selected ? 0xecffe1 : view.tile.symbol === 'JK' ? 0xffedb3 : 0xffffff;
    view.selection.clear();
    if (selected) {
      const radius = Math.max(9, size * 0.1);
      const centerX = size - radius / 2;
      const centerY = radius / 2;
      view.selection.circle(centerX, centerY, radius).fill(0x197a59).stroke({ color: 0xfff4d9, width: 2 });
      view.selection.moveTo(centerX - radius * 0.4, centerY).lineTo(centerX - radius * 0.1, centerY + radius * 0.3).lineTo(centerX + radius * 0.45, centerY - radius * 0.35).stroke({ color: 0xffffff, width: 2.3 });
    }
    view.container.cursor = this.disabled ? 'default' : 'pointer';
  }

  setAssistance(visible, enabled, jokerEnabled = false) {
    this.jokerEnabled = jokerEnabled;
    this.assistanceVisible = visible;
    this.assistanceEnabled = enabled;
    for (const view of this.tileViews) {
      view.change.visible = visible && !(jokerEnabled && view.tile.symbol === 'JK');
      view.change.eventMode = visible && enabled ? 'static' : 'none';
      view.change.cursor = enabled ? 'pointer' : 'default';
      view.change.alpha = enabled ? 1 : .35;
    }
    this.application?.render();
  }

  setSelectedTiles(selectedIds) {
    const changed = this.tileViews.filter(view => selectedIds.has(view.tile.id) !== this.selectedIds.has(view.tile.id));
    this.selectedIds = new Set(selectedIds);
    for (const view of this.tileViews) this.paintSelection(view);
    if (changed.length) {
      cancelAnimationFrame(this.selectionFrame);
      for (const view of this.tileViews) view.container.y = view.targetY;
      const started = performance.now();
      const animate = now => {
        const progress = Math.min(1, (now - started) / 280);
        const lift = Math.sin(progress * Math.PI) * (1 - progress) * 12;
        for (const view of changed) view.container.y = view.targetY - lift;
        this.application?.render();
        if (progress < 1) this.selectionFrame = requestAnimationFrame(animate);
        else { for (const view of changed) view.container.y = view.targetY; this.application?.render(); }
      };
      this.selectionFrame = requestAnimationFrame(animate);
    }
    this.application?.render();
  }

  destroy() {
    cancelAnimationFrame(this.selectionFrame);
    this.resizeObserver?.disconnect();
    if (this.application?.renderer) this.application.destroy(true, { children: true });
  }
}
